import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  ArrowLeft,
  Plus,
  Play,
  Pause,
  Trash2,
  Pencil,
  Loader2,
  Clock,
  Webhook,
  History,
  X,
  Copy,
  Check,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  createReport,
  deleteReport,
  listReportRuns,
  listReports,
  runReportNow,
  toggleReportPaused,
  updateReport,
  type Report,
  type ReportRun,
  type ReportScheduleKind,
} from "@/lib/reports.functions";
import { AdminGate } from "@/components/admin/AdminGate";

export const Route = createFileRoute("/admin/reports")({
  head: () => ({
    meta: [
      { title: "Reports — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => <AdminGate route="/admin/reports"><ReportsPage /></AdminGate>,
});

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function scheduleLabel(r: Report): string {
  if (r.schedule_kind === "none" || r.schedule_hour === null) return "Manual";
  const hh = String(r.schedule_hour).padStart(2, "0");
  const mm = String(r.schedule_minute).padStart(2, "0");
  if (r.schedule_kind === "daily") return `Diário · ${hh}:${mm}`;
  if (r.schedule_kind === "weekly")
    return `Semanal · ${WEEKDAYS[r.schedule_weekday ?? 1]} ${hh}:${mm}`;
  return `Mensal · dia ${r.schedule_day ?? 1} · ${hh}:${mm}`;
}

function formatBR(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

function ReportsPage() {
  const navigate = useNavigate();
  const [authReady, setAuthReady] = useState(false);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) navigate({ to: "/admin" });
      else setAuthReady(true);
    });
  }, [navigate]);

  const qc = useQueryClient();
  const { data: reports, isLoading } = useQuery({
    queryKey: ["reports"],
    queryFn: () => listReports(),
    enabled: authReady,
  });

  const [editing, setEditing] = useState<Report | null>(null);
  const [creating, setCreating] = useState(false);
  const [viewingRun, setViewingRun] = useState<{ report: Report; run: ReportRun } | null>(null);
  const [historyFor, setHistoryFor] = useState<Report | null>(null);

  const runMut = useMutation({
    mutationFn: (id: string) => runReportNow({ data: { reportId: id } }),
    onSuccess: (run, id) => {
      qc.invalidateQueries({ queryKey: ["reports"] });
      qc.invalidateQueries({ queryKey: ["report-runs", id] });
      const r = reports?.find((x) => x.id === id);
      if (r) setViewingRun({ report: r, run });
      toast.success("Report gerado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const pauseMut = useMutation({
    mutationFn: ({ id, paused }: { id: string; paused: boolean }) =>
      toggleReportPaused({ data: { id, paused } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reports"] }),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => deleteReport({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reports"] });
      toast.success("Report excluído");
    },
  });

  if (!authReady) return <div className="min-h-screen flex items-center justify-center text-white/50">Carregando…</div>;

  return (
    <div className="min-h-screen relative overflow-hidden">
      
      <div className="relative px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <Link
                to="/admin"
                className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 mb-2"
              >
                <ArrowLeft className="h-3 w-3" /> Painel de Controle
              </Link>
              <h1 className="text-3xl font-bold text-white tracking-tight">Reports</h1>
              <p className="text-sm text-white/60 mt-1">
                Gerar ou agendar Relatórios. Gere com 1 clique ou deixe o Fision rodar sozinho.
              </p>
            </div>
            <Button
              onClick={() => setCreating(true)}
              className="bg-[#f5b022] hover:bg-[#e09f17] text-[#13123a] font-semibold"
            >
              <Plus className="h-4 w-4 mr-2" /> Novo report
            </Button>
          </div>

          {isLoading && (
            <div className="text-white/50 text-sm py-10 text-center">Carregando…</div>
          )}

          {reports && reports.length === 0 && (
            <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-10 text-center">
              <p className="text-white/70">Nenhum report ainda.</p>
              <p className="text-xs text-white/40 mt-1">
                Crie o primeiro — ex.: "Status diário de pós às 20h".
              </p>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            {reports?.map((r) => (
              <div
                key={r.id}
                className="group rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:border-[#f5b022]/30 transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-white font-semibold truncate">{r.name}</h3>
                    <div className="flex items-center gap-3 text-xs text-white/50 mt-1">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {scheduleLabel(r)}
                      </span>
                      {r.webhook_url && (
                        <span className="inline-flex items-center gap-1 text-blue-300/70">
                          <Webhook className="h-3 w-3" /> webhook
                        </span>
                      )}
                      {r.paused && (
                        <span className="text-admin-accent/80 uppercase tracking-wide">pausado</span>
                      )}
                    </div>
                  </div>
                </div>

                <p className="mt-3 text-sm text-white/70 line-clamp-2">{r.prompt}</p>

                <div className="mt-3 text-[11px] text-white/40 flex items-center justify-between">
                  <span>Último: {formatBR(r.last_run_at)}</span>
                  {r.next_run_at && !r.paused && (
                    <span>Próximo: {formatBR(r.next_run_at)}</span>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => runMut.mutate(r.id)}
                    disabled={runMut.isPending && runMut.variables === r.id}
                    className="bg-[#f5b022] hover:bg-[#e09f17] text-[#13123a] font-semibold"
                  >
                    {runMut.isPending && runMut.variables === r.id ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Play className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Gerar agora
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setHistoryFor(r)}
                    className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white"
                  >
                    <History className="h-3.5 w-3.5 mr-1.5" /> Histórico
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(r)}
                    className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {r.schedule_kind !== "none" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => pauseMut.mutate({ id: r.id, paused: !r.paused })}
                      className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white"
                    >
                      {r.paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (confirm(`Excluir "${r.name}"?`)) delMut.mutate(r.id);
                    }}
                    className="bg-white/5 border-red-500/20 text-red-300 hover:bg-red-500/10 hover:text-red-200"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {(creating || editing) && (
        <ReportFormDialog
          report={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["reports"] });
            setCreating(false);
            setEditing(null);
          }}
        />
      )}

      {viewingRun && (
        <RunDialog
          report={viewingRun.report}
          run={viewingRun.run}
          onClose={() => setViewingRun(null)}
        />
      )}

      {historyFor && (
        <HistoryDialog
          report={historyFor}
          onClose={() => setHistoryFor(null)}
          onOpenRun={(run) => {
            setHistoryFor(null);
            setViewingRun({ report: historyFor, run });
          }}
        />
      )}
    </div>
  );
}

function ReportFormDialog({
  report,
  onClose,
  onSaved,
}: {
  report: Report | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(report?.name ?? "");
  const [prompt, setPrompt] = useState(report?.prompt ?? "");
  const [kind, setKind] = useState<ReportScheduleKind>(report?.schedule_kind ?? "none");
  const [hour, setHour] = useState<number>(report?.schedule_hour ?? 20);
  const [minute, setMinute] = useState<number>(report?.schedule_minute ?? 0);
  const [weekday, setWeekday] = useState<number>(report?.schedule_weekday ?? 1);
  const [day, setDay] = useState<number>(report?.schedule_day ?? 1);
  const [webhook, setWebhook] = useState(report?.webhook_url ?? "");

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = {
        name,
        prompt,
        schedule_kind: kind,
        schedule_hour: kind === "none" ? null : hour,
        schedule_minute: minute,
        schedule_weekday: kind === "weekly" ? weekday : null,
        schedule_day: kind === "monthly" ? day : null,
        webhook_url: webhook.trim() || null,
      };
      if (report) {
        return updateReport({ data: { ...payload, id: report.id } });
      }
      return createReport({ data: payload });
    },
    onSuccess: () => {
      toast.success(report ? "Report atualizado" : "Report criado");
      onSaved();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-[#13123a] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>{report ? "Editar report" : "Novo report"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="r-name">Nome</Label>
            <Input
              id="r-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Status diário de pós"
              className="bg-white/5 border-white/10"
            />
          </div>
          <div>
            <Label htmlFor="r-prompt">O que o Fision deve responder</Label>
            <Textarea
              id="r-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={5}
              placeholder='Ex.: "Total de sessões hoje somadas e separadas por cada uma das 6 pós, e número de leads qualificados de pós coletados hoje."'
              className="bg-white/5 border-white/10"
            />
            <p className="text-[11px] text-white/40 mt-1">
              O Fision já tem acesso a sessões, page views por LP, leads (com classificação e pós) e cliques.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Agendamento</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as ReportScheduleKind)}>
                <SelectTrigger className="bg-white/5 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum (manual)</SelectItem>
                  <SelectItem value="daily">Diário</SelectItem>
                  <SelectItem value="weekly">Semanal</SelectItem>
                  <SelectItem value="monthly">Mensal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {kind !== "none" && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Hora</Label>
                  <Input
                    type="number"
                    min={0}
                    max={23}
                    value={hour}
                    onChange={(e) => setHour(parseInt(e.target.value || "0", 10))}
                    className="bg-white/5 border-white/10"
                  />
                </div>
                <div>
                  <Label>Min</Label>
                  <Input
                    type="number"
                    min={0}
                    max={59}
                    value={minute}
                    onChange={(e) => setMinute(parseInt(e.target.value || "0", 10))}
                    className="bg-white/5 border-white/10"
                  />
                </div>
              </div>
            )}
          </div>

          {kind === "weekly" && (
            <div>
              <Label>Dia da semana</Label>
              <Select value={String(weekday)} onValueChange={(v) => setWeekday(parseInt(v, 10))}>
                <SelectTrigger className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((d, i) => (
                    <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {kind === "monthly" && (
            <div>
              <Label>Dia do mês (1–28)</Label>
              <Input
                type="number"
                min={1}
                max={28}
                value={day}
                onChange={(e) => setDay(parseInt(e.target.value || "1", 10))}
                className="bg-white/5 border-white/10"
              />
            </div>
          )}

          <div>
            <Label htmlFor="r-webhook">Webhook (opcional)</Label>
            <Input
              id="r-webhook"
              value={webhook}
              onChange={(e) => setWebhook(e.target.value)}
              placeholder="https://discord.com/api/webhooks/..."
              className="bg-white/5 border-white/10"
            />
            <p className="text-[11px] text-white/40 mt-1">
              Se preenchido, o resultado é enviado pra cá nos runs automáticos. Detecta Discord automaticamente.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white">
            Cancelar
          </Button>
          <Button
            onClick={() => saveMut.mutate()}
            disabled={!name || !prompt || saveMut.isPending}
            className="bg-[#f5b022] hover:bg-[#e09f17] text-[#13123a] font-semibold"
          >
            {saveMut.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RunDialog({ report, run, onClose }: { report: Report; run: ReportRun; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(run.output ?? "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col bg-[#13123a] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <span>{report.name}</span>
            <button onClick={onClose} className="text-white/40 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </DialogTitle>
          <div className="text-xs text-white/50">
            {formatBR(run.generated_at)} · {run.triggered_by === "manual" ? "manual" : "automático"}
          </div>
        </DialogHeader>
        <div className="overflow-y-auto pr-2">
          {run.status === "error" ? (
            <div className="text-red-300 text-sm">{run.error ?? "Erro desconhecido"}</div>
          ) : (
            <div className="prose prose-sm prose-invert max-w-none">
              <ReactMarkdown>{run.output ?? ""}</ReactMarkdown>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={copy}
            className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white"
          >
            {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
            {copied ? "Copiado" : "Copiar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({
  report,
  onClose,
  onOpenRun,
}: {
  report: Report;
  onClose: () => void;
  onOpenRun: (run: ReportRun) => void;
}) {
  const { data: runs, isLoading } = useQuery({
    queryKey: ["report-runs", report.id],
    queryFn: () => listReportRuns({ data: { reportId: report.id } }),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-[#13123a] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>Histórico · {report.name}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-1">
          {isLoading && <div className="text-sm text-white/50">Carregando…</div>}
          {runs && runs.length === 0 && (
            <div className="text-sm text-white/50">Sem execuções ainda.</div>
          )}
          {runs?.map((r) => (
            <button
              key={r.id}
              onClick={() => onOpenRun(r)}
              className="w-full text-left flex items-center justify-between px-3 py-2.5 rounded-lg border border-white/5 bg-white/[0.02] hover:bg-white/[0.05]"
            >
              <div>
                <div className="text-sm text-white/90">{formatBR(r.generated_at)}</div>
                <div className="text-[11px] text-white/40">
                  {r.triggered_by === "manual" ? "manual" : "automático"}
                  {r.status === "error" && " · erro"}
                </div>
              </div>
              <span
                className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${
                  r.status === "ok"
                    ? "text-emerald-300 border-emerald-400/20 bg-emerald-400/5"
                    : "text-red-300 border-red-400/20 bg-red-400/5"
                }`}
              >
                {r.status}
              </span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
